import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import { PopperProps } from '@mui/material/Popper';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { BpDesignSystemTheme, useBpDesignTheme } from '../../setup/theme/theme';
import { styled } from '../../styles';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { mergeClassNames } from '../../utils';
import { useDebounce } from '../../utils/useDebounce';
import { MAX_TERM_LENGTH } from '../Embolden';
import { FormHelperText } from '../Form/FormHelperText';
import { FormLabel } from '../Form/FormLabel';
import { LabelWithIcon } from '../Form/LabelWithIcon';
import { Typography } from '../Typography/Typography';
import { AutocompleteInput } from './SelectElements/elements/AutocompleteInput';
import { Chips } from './SelectElements/elements/Chips';
import { Group } from './SelectElements/elements/Group';
import { Option } from './SelectElements/elements/Option';
import { PaperMuiComponent } from './SelectElements/elements/PaperMuiComponent';
import { PopperAdapter } from './SelectElements/elements/PopperAdapter';
import { StyledFormControl } from './SelectElements/elements/StyledFormControl';
import { ListboxComponent } from './SelectElements/MuiSelectAutocompleteVirtualComponents';
import { isCreatableOption } from './SelectElements/utils/guards';
import { useChangeHandler } from './SelectElements/utils/useChangeHandler';
import { compareOptions, useDropdownOptions } from './SelectElements/utils/useDropdownOptions';
import { useFilterHandler } from './SelectElements/utils/useFilterHandler';
import { useForceCloseEvent } from './SelectElements/utils/useForceCloseEvent';
import {
  CreatableOption,
  InternalAutocompleteRenderInputParams,
  MultiSelectProps,
  OptionTypeWithCode,
  SelectProps
} from './types';

const EMPTY: OptionTypeWithCode[] = [];

export { FORCE_CLOSE_EVENT } from './SelectElements/utils/useForceCloseEvent';

export function Select<VALUE = string, MULTI extends boolean = false>({
  // basic
  name,
  id,
  style,
  className,
  description,
  label,
  placeholder,
  autoComplete,
  disabled,
  disableClearable,
  error,
  required,
  fullWidth = true,
  dataTest = 'select',
  loading,
  debounce = 300,
  helperText,
  errorText,
  tooltipText,
  searchMode,
  visibleElementsWhenSelected = {
    label: true,
    code: true
  },

  // values & options
  options,
  uniqueOptions,
  value,
  valueOptions,

  // components props
  menuProps,
  listBoxProps,
  chipProps,
  // custom components
  components,

  // handlers
  onOpen,
  onOptionChange,
  onChange,
  onSearch,
  onInit,
  onCreate,
  onBlur,
  onClose,
  autoFocus,
  sx,
  inputProps,
  ...rest // multiple, closeOnSelect
}: SelectProps<MULTI, VALUE>) {
  const { intl, format } = useIntlBds();
  const generatedId = useId();
  const newId = id ?? generatedId;
  const { tokens } = useBpDesignTheme();
  const { multiple, closeOnSelect } = (rest ?? {}) as unknown as MultiSelectProps<VALUE>;
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [willTriggerDebounceSearch, setWillTriggerDebounceSearch] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>();
  const isCreatable = !!onCreate;
  const [searchTerm, setSearchTerm] = useState('');
  const isLoading = loading || willTriggerDebounceSearch;
  const hasFormControl = !!label || !!description || !!helperText || !!errorText;
  const formControlProps = hasFormControl ? { required, error, fullWidth, dataTest: `${dataTest}-form-control` } : {};
  const usePortal = menuProps?.usePortal ?? true;
  useForceCloseEvent(setOpen);

  // Defer openOnStart by one render frame so MUI Autocomplete processes options first.
  // This prevents an empty menu flash when options are loaded asynchronously on open.
  useEffect(() => {
    if (menuProps?.openOnStart) {
      setOpen(true);
    }
  }, [menuProps?.openOnStart]);

  const dropdownOptions = useDropdownOptions(
    value,
    valueOptions,
    options,
    menuProps?.sort,
    menuProps?.showUnmatchedValues ?? true,
    uniqueOptions
  );
  const handleOptionsChange = useChangeHandler<VALUE>(multiple, onChange, onOptionChange, onCreate);
  const handleFilterOptions = useFilterHandler<VALUE>(
    onSearch,
    menuProps?.createOptionPrefix ?? 'Add',
    isCreatable,
    loading
  );

  const handleSearch = useDebounce(
    onSearch
      ? (term: string) => {
          onSearch(term, 'input');
          setWillTriggerDebounceSearch(false);
        }
      : noop,
    debounce
  );

  const onInputChangeHandler = useCallback(
    (event: React.ChangeEvent<unknown>, value: string, reason: AutocompleteInputChangeReason) => {
      setSearchTerm(searchMode ? value.substring(0, MAX_TERM_LENGTH) : value);
      if (onSearch) {
        if (reason === 'input' || reason === 'clear' || (reason === 'reset' && value === '')) {
          setWillTriggerDebounceSearch(debounce !== 0);
          handleSearch(value);
        }
      }
    },
    [debounce, handleSearch, onSearch, searchMode]
  );

  const regainFocusOnChipClick = () => {
    autocompleteRef.current?.querySelector('input')?.focus();
  };

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      onBlur?.(e);
    },
    [onBlur]
  );

  /**
   * trigger onSearch on component initialization
   */

  // biome-ignore lint/correctness/useExhaustiveDependencies: run it just once
  useEffect(() => {
    if (onInit && !!value) {
      if (multiple) {
        onInit((value as VALUE[]).length ? (value as VALUE[] & VALUE) : undefined);
      } else {
        onInit(value as (VALUE[] & VALUE) | undefined);
      }
    } else {
      onSearch?.('', 'initial');
    }
  }, []);

  const selectedOptions = multiple ? (dropdownOptions.selected ?? null) : (dropdownOptions.selected?.[0] ?? null);

  const Wrapper = hasFormControl ? StyledFormControl : 'div';
  return (
    <Wrapper {...formControlProps} data-test={dataTest}>
      {!!label && (
        <>
          {!tooltipText && (
            <FormLabel required={required} htmlFor={newId} dataTest={`${dataTest}-label`} shrink>
              {label}
            </FormLabel>
          )}
          {tooltipText && (
            <LabelWithIcon
              required={required}
              htmlFor={newId}
              dataTest={`${dataTest}-label`}
              slotProps={{ tooltipProps: { title: tooltipText } }}
              shrink
            >
              {label}
            </LabelWithIcon>
          )}
        </>
      )}

      {!!description && <StyledDescription variant="body2">{description}</StyledDescription>}
      <Autocomplete
        data-version="current"
        data-component-role="autocomplete"
        id={newId}
        fullWidth={hasFormControl ? true : fullWidth}
        noOptionsText={menuProps?.noOptionsText}
        options={isLoading && !menuProps?.showOptionsDuringLoading ? EMPTY : dropdownOptions.list}
        disabled={disabled}
        open={open}
        style={getStyle(tokens, style, error)}
        data-test={`${dataTest}-field`}
        className={mergeClassNames(className, {
          'single-select': !multiple,
          'multi-select': multiple
        })}
        disablePortal={!usePortal}
        onChange={(e, value, reason) => {
          // in this case it is React.ChangeEvent<HTMLInputElement> not React.ChangeEvent<{}>
          if (!(reason === 'clear' && (e as React.ChangeEvent)?.target?.tagName === 'INPUT')) {
            handleOptionsChange(value);
          }
          if (searchMode) {
            setSearchTerm('');
            onBlur?.(e as React.FocusEvent);
          }
        }}
        onOpen={(e) => {
          // in this case it is React.ChangeEvent<HTMLInputElement> not React.ChangeEvent<{}>
          if (
            disabled ||
            hasParent(e as React.ChangeEvent, 'uds-chip') ||
            hasParent(e as React.ChangeEvent, 'muiSelect-close-icon')
          ) {
            return;
          }
          onOpen?.(e);
          setOpen(true);
        }}
        onClose={() => {
          onClose?.();
          setOpen(false);
        }}
        onInputChange={onInputChangeHandler}
        loading={isLoading}
        inputValue={searchMode ? searchTerm : undefined}
        onBlur={handleBlur}
        value={selectedOptions}
        selectOnFocus={isCreatable ? true : undefined}
        clearOnBlur={isCreatable ? true : undefined}
        freeSolo={isCreatable ? true : undefined}
        handleHomeEndKeys={isCreatable ? true : undefined}
        blurOnSelect={!!searchMode}
        disabledItemsFocusable={false}
        multiple={multiple}
        sx={sx}
        disableCloseOnSelect={multiple && !closeOnSelect}
        filterOptions={menuProps?.filterOptions ?? handleFilterOptions}
        ref={autocompleteRef}
        isOptionEqualToValue={(option1, option2) => isEqual(option1.value, option2.value)}
        slots={{
          paper: (paperProps) =>
            (components?.Paper ?? PaperMuiComponent)({
              ...menuProps,
              paperProps,
              showLoadMore: menuProps?.showLoadMore
            }),
          popper: components?.Popper ?? (PopperAdapter(menuProps?.placement) as React.ComponentType<PopperProps>)
        }}
        slotProps={{
          listbox: {
            ...listBoxProps,
            component:
              components?.MenuList ??
              (ListboxComponent as React.ComponentType<React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>>)
          }
        }}
        getOptionLabel={(option) => getOptionLabel(option)}
        renderGroup={components?.Group ?? Group}
        groupBy={menuProps?.group ? (option) => (option as OptionTypeWithCode)?.groupName ?? '' : undefined}
        renderInput={(params) =>
          (components?.Input ?? AutocompleteInput)({
            params: params as InternalAutocompleteRenderInputParams,
            onFocus: setFocused,
            onClear: () => handleOptionsChange(null),
            selectedOptions,
            loading: isLoading,
            visibleElementsWhenSelected,
            placeholder,
            autoComplete,
            open,
            name,
            autoFocus,
            inputProps
          })
        }
        renderTags={(selectedOptions: OptionTypeWithCode[], getTagProps) => {
          const isFocused = focused && !loading;
          const chipsLimit = chipProps?.limit?.count ?? 999;
          const limit = isFocused && !chipProps?.limit?.permanent ? 999 : chipsLimit;
          const endText = selectedOptions.length <= limit ? 0 : selectedOptions.length - limit;
          return (components?.Chips ?? Chips)({
            selectedOptions: menuProps?.sort ? [...selectedOptions].sort(compareOptions) : selectedOptions,
            getTagProps,
            visibleElementsWhenSelected,
            isFocused,
            loading,
            onBeforeChipClick: regainFocusOnChipClick,
            endText:
              chipProps?.limit?.text ??
              (!!endText ? format(intl.Select.Chips.endText, { endText: endText.toString() }) : ''),
            ...chipProps
          });
        }}
        renderOption={(props, option, state) =>
          (components?.Option ?? Option)({
            optionProps: props,
            option,
            state,
            dataTest,
            searchTerm,
            noWrapSubLabel: menuProps?.noWrapSubLabel,
            iconSize: menuProps?.iconSize
          })
        }
        filterSelectedOptions={
          typeof menuProps?.hideSelectedOptions === 'boolean'
            ? menuProps.hideSelectedOptions
            : !!autoComplete || !!onSearch || !!multiple
        }
        disableClearable={disableClearable}
      />
      <div style={{ height: '1px', clear: 'both' }} />
      <FormHelperText helperText={helperText} errorText={errorText} error={error} />
    </Wrapper>
  );
}

function hasParent(e: { target?: Element } | undefined, dataTest: string): boolean {
  return !!e?.target?.closest(`[data-test="${dataTest}"]`);
}

function noop() {}

function getStyle(
  tokens: BpDesignSystemTheme['tokens'],
  style?: React.CSSProperties,
  error?: boolean
): React.CSSProperties | undefined {
  if (error) {
    return {
      ...style,
      borderColor: tokens.colorAlert
    };
  }
  return style;
}

const getOptionLabel = (option: OptionTypeWithCode | CreatableOption | string): string => {
  if (typeof option === 'string') {
    return option;
  }
  if (isCreatableOption(option)) {
    return `${option.creatableTitle}`;
  }
  return option.label ? `${option.label}` : '';
};

const StyledDescription = styled(Typography)`
    && {
        margin-bottom: 2px;
    }
`;
